import { Box, Drawer } from "@mantine/core";
import { useAtom } from "jotai";
import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";

import Button from "components/Button";
import Flex from "components/Flex";
import StickyButtonsBox from "components/StickyButtonsBox";
import Text from "components/Text";

import { pageDrawerAtom } from "atoms/pageDrawer";
import { useClientTranslation } from "hooks/useClientTranslation";

import { useStyles } from "./styles";

type BottomDrawerProps = {
  children: ReactNode;
  heading?: string;
  headingIcon?: JSX.Element;
  callToActionText?: string;
  callToActionOnClick?: () => void;
  isOpen?: boolean;
  closeOnClickOutside?: boolean;
  stickyBoxShadow?: boolean;
  withStickyButton?: boolean;
  fullScreen?: boolean;
};

export const BottomDrawer = ({
  children,
  heading,
  headingIcon,
  callToActionText,
  callToActionOnClick,
  isOpen,
  closeOnClickOutside,
  stickyBoxShadow = false,
  fullScreen = false,
  withStickyButton = true,
}: BottomDrawerProps) => {
  const { classes } = useStyles({ fullScreen });
  const [isDrawerOpen, toggleDrawer] = useAtom(pageDrawerAtom);

  const location = useLocation();

  const { t } = useClientTranslation();

  // Makes sure the drawer always starts out closed on page transition
  useEffect(() => {
    toggleDrawer(false);
  }, [location, toggleDrawer]);

  // Use open state passed in via prop over global atom open state
  const drawerOpen = isOpen !== undefined ? isOpen : isDrawerOpen;

  return (
    <Drawer
      opened={drawerOpen}
      onClose={toggleDrawer}
      withCloseButton={false}
      overlayColor={"#12191F"}
      overlayOpacity={0.35}
      closeOnClickOutside={closeOnClickOutside}
      size="xl"
      position="bottom"
      classNames={{ drawer: classes.drawer }}
      zIndex={3}
    >
      <Box px={24} pt={24} pb={40}>
        {heading && (
          <Flex direction="row" mb={8} gap={8} align="center">
            {headingIcon}
            <Text sizeVariant="xl" as="h1" weight={600}>
              {heading}
            </Text>
          </Flex>
        )}
        {children}
      </Box>
      {withStickyButton && (
        <StickyButtonsBox shadow={stickyBoxShadow}>
          <Button onClick={callToActionOnClick || toggleDrawer}>{callToActionText || t("global.close")}</Button>
        </StickyButtonsBox>
      )}
    </Drawer>
  );
};
